import { BodyRegular, Headline3 } from 'components/Elements';
import { BuildCourseContext } from 'contextProviders/EditCourseContext';
import React from 'react';
import { styled } from 'styled-components';
import { breakpoints, sizes } from 'theme';
import strings from '../../../../strings/strings.json';
import ReviewGeneratedAudioComponent from './reviewGeneratedContentComponents/ReviewGeneratedAudioComponent';
import ReviewGeneratedTextComponent from './reviewGeneratedContentComponents/ReviewGeneratedTextComponent';
import { ModuleItem } from './ModuleItem';
import { GenerateType } from 'models/Activity';
import { Course } from 'models/Course';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import ReviewGeneratedModuleQuestionnaire from './reviewGeneratedContentComponents/ReviewGeneratedModuleQuestionnaire';
import { StepId } from 'pages/build-course/CourseBuilderPage';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { useNavigate } from 'react-router-dom';
import Hint from 'components/Hint';
import { GenerateItem, getStatus, Status } from './TaskGenerateItem';
import { ModuleListItem } from './ModuleListItem';

type CourseAIGeneratedContentComponentProps = {
    showIncompleteStepsWarning?: boolean;
};

export default function CourseGenerateContentComponent({
    showIncompleteStepsWarning,
}: CourseAIGeneratedContentComponentProps): JSX.Element {
    const context = React.useContext(BuildCourseContext);
    const navigate = useNavigate();

    const [taskIdToReview, setTaskIdToReview] = React.useState<string>('');

    const moduleItems = getModuleItems(context.course!);
    const nothingToGenerate = moduleItems.flatMap((module) => module.generateItems).length === 0;

    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
        const someItemsAreGenerating = getModuleItems(context.course!).some((module) =>
            module.generateItems.some((item) => getStatus(item) === Status.STARTED),
        );

        if (someItemsAreGenerating && !intervalRef.current) {
            // Start polling
            intervalRef.current = setInterval(() => {
                context.refetchCourse();
            }, 8000);
        }

        if (!someItemsAreGenerating && intervalRef.current) {
            // Stop polling
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [context.course]); // eslint-disable-line

    if (nothingToGenerate) {
        return (
            <NoContentContainer>
                <GenerateContentHeader>
                    <Headline3>{strings.courseEditorPage.steps.aiGeneratedContent.title}</Headline3>
                </GenerateContentHeader>
                <ModulesList>
                    <BodyRegular $bold>
                        {strings.courseAIGeneratedContentComponent.noContentGenerated.text}
                    </BodyRegular>
                    <Hint hint={strings.courseAIGeneratedContentComponent.noContentGenerated.hintText} />
                    <PrimaryButton
                        title={strings.courseAIGeneratedContentComponent.noContentGenerated.button.title}
                        aria={strings.courseAIGeneratedContentComponent.noContentGenerated.button.aria}
                        fullwidth
                        onClick={() =>
                            navigate(`/build-course/${context.course!.id}`, {
                                state: {
                                    stepId: 'courseStructureAndContent' as StepId,
                                },
                            })
                        }
                    />
                </ModulesList>
            </NoContentContainer>
        );
    }

    if (taskIdToReview) {
        const taskContent = moduleItems
            .flatMap((module) => module.generateItems)
            .find((task) => task.id === taskIdToReview);

        if (taskContent) {
            switch (taskContent.generateType) {
                case GenerateType.TEXT_TO_AUDIO:
                    return (
                        <ReviewGeneratedAudioComponent item={taskContent} backButton={() => setTaskIdToReview('')} />
                    );
                case GenerateType.AUDIO_TO_TEXT:
                case GenerateType.VIDEO_TO_TEXT:
                    return (
                        <ReviewGeneratedTextComponent item={taskContent} backButton={() => setTaskIdToReview('')} />
                    );
                case GenerateType.MODULE_QUESTIONNAIRE:
                    return (
                        <ReviewGeneratedModuleQuestionnaire
                            item={taskContent}
                            backButton={() => setTaskIdToReview('')}
                        />
                    );
                default:
                    return <></>;
            }
        }
    }

    return (
        <ComponentContainer>
            <GenerateContentHeader>
                <Headline3>{strings.courseEditorPage.steps.aiGeneratedContent.title}</Headline3>
            </GenerateContentHeader>
            {showIncompleteStepsWarning && (
                <Notification
                    title={strings.courseEditorPage.generatedContentToBeReviewed.title}
                    notificationType={NotificationType.WARNING}
                    maxWidth="100%"
                />
            )}
            <Hint hint={strings.courseAIGeneratedContentComponent.hint} />
            <ModulesList>
                {moduleItems
                    .filter((module) => module.generateItems.length)
                    .map((module, moduleIndex) => {
                        return (
                            <ModuleListItem
                                key={moduleIndex}
                                module={module}
                                courseId={context.course!.id}
                                setTaskIdToReview={setTaskIdToReview}
                            />
                        );
                    })}
            </ModulesList>
        </ComponentContainer>
    );
}

const ComponentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

const NoContentContainer = styled(ComponentContainer)`
    max-width: 600px;
`;

const GenerateContentHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${sizes.spacingMd};

    @media (max-width: ${breakpoints.md}) {
        flex-direction: column;
        align-items: unset;
    }
`;

const ModulesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

function getModuleItems(course: Course): ModuleItem[] {
    return course.modules
        .sort((a, b) => a.order - b.order)
        .map((module) => {
            const generateItems: GenerateItem[] = module.activities
                .sort((a, b) => a.order - b.order)
                .flatMap((task) => {
                    if (!(task.generateContentStatuses ?? []).length) return [];

                    return task.generateContentStatuses.map((status) => {
                        const { generateType } = status;

                        let mediaContent = '';
                        let textContent = '';

                        switch (status.generateType) {
                            default:
                            case GenerateType.TEXT_TO_AUDIO:
                                mediaContent = status.s3Filepath;
                                textContent = task.content;
                                break;
                            case GenerateType.AUDIO_TO_TEXT:
                                mediaContent = task.audioDocument?.s3Filepath ?? '';
                                textContent = status.s3Filepath;
                                break;
                            case GenerateType.VIDEO_TO_TEXT:
                                mediaContent = task.videoDocument?.s3Filepath ?? '';
                                textContent = status.s3Filepath;
                                break;
                            case GenerateType.MODULE_QUESTIONNAIRE:
                                textContent = status.s3Filepath;
                        }

                        return {
                            activityId: task.id,
                            activityType: task.type,
                            activityOrder: task.order,
                            activityTitle: task.title,
                            taskBuildMethod: task.buildMethod,
                            textContent,
                            mediaContent,
                            id: status.id,
                            generateType,
                            dateCreated: status.dateCreated,
                            dateUpdated: status.dateUpdated,
                            dateGenerated: status.dateGenerated,
                            s3Filepath: status.s3Filepath,
                            dateErrored: status.dateErrored,
                            error: status.error,
                            dateApproved: status.dateApproved,
                            dateGenerateStart: status.dateGenerateStart,
                        };
                    });
                });

            return {
                moduleOrder: module.order,
                moduleTitle: module.title,
                generateItems,
            };
        });
}
